import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';

import SectionHeader from '../SectionHeader';
import FeaturesGrid from './components/FeaturesGrid';
import NewFeaturesModal from './components/NewFeaturesModal';

import { fetchNewFeatures } from '@home/services/newFeatures';

import {
  isSafeUrlCollectionTargetUrl,
  navigateUrlCollectionUrl,
  openUrlCollectionExternalUrl,
} from '@home/utils/urlCollectionData';
import { logUrlCollectionButtonClick, URL_COLLECTION_LOG_CONFIG } from '../MyShortcuts/_hooks/utils';
import { getNewFeatureDisplayName } from './utils';

import type { INewFeatureItem } from '@home/types/urlCollection';

import ICON_FEATURE_BUBBLE from '@home/assets/svg/urlCollection/feature-bulb.svg';

export interface INewFeaturesProps extends IBaseProps {
  title?: string;
  actionText?: string;
  homeCount?: number;
  batchSize?: number;
}

const NewFeatures: React.FC<INewFeaturesProps> = (props) => {
  const { style, className, title, actionText, homeCount = 2, batchSize = 20 } = props;
  const { locale, formatMessage } = useIntl();
  const [features, setFeatures] = useState<INewFeatureItem[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const [isNewFeaturesModalVisible, setIsNewFeaturesModalVisible] = useState(false);
  const loadSeqRef = useRef(0);
  const titleText =
    title ||
    formatMessage({
      id: 'dbh_url_collection_new_features_title',
      defaultMessage: 'New Features',
    });
  const actionTextValue =
    actionText ||
    formatMessage({
      id: 'dbh_url_collection_more',
      defaultMessage: 'More',
    });
  const homeItems = features.slice(0, homeCount);
  const emptyFeatures = !featuresLoading && !features.length;
  const canOpenModal = !featuresLoading && features.length > 0;

  const loadFeatures = async (): Promise<void> => {
    setFeaturesLoading(true);
    const currentSeq = loadSeqRef.current + 1;
    loadSeqRef.current = currentSeq;

    try {
      const response = await fetchNewFeatures({});
      const nextFeatures = Array.isArray(response?.features) ? response.features : [];

      if (loadSeqRef.current !== currentSeq) {
        return;
      }

      setFeatures(nextFeatures);
    } catch (error) {
      if (loadSeqRef.current !== currentSeq) {
        return;
      }

      setFeatures([]);
    } finally {
      if (loadSeqRef.current === currentSeq) {
        setFeaturesLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadFeatures();

    return () => {
      loadSeqRef.current += 1;
    };
  }, []);

  const handleFeatureClick = (item: INewFeatureItem): void => {
    if (!isSafeUrlCollectionTargetUrl(item.redirect_path)) {
      return;
    }

    logUrlCollectionButtonClick(URL_COLLECTION_LOG_CONFIG.clickFeature, {
      id: item.id,
      name: getNewFeatureDisplayName(locale, item),
      url: item.redirect_path,
      system: item.system,
      page_id: item.page_id,
    });

    if (navigateUrlCollectionUrl(item.redirect_path)) {
      return;
    }

    openUrlCollectionExternalUrl(item.redirect_path);
  };

  const handleOpenModal = (): void => {
    if (!canOpenModal) {
      return;
    }

    logUrlCollectionButtonClick(URL_COLLECTION_LOG_CONFIG.clickMore, {
      module: 'new_features',
    });
    setIsNewFeaturesModalVisible(true);
  };

  return (
    <>
      <div style={style} className={classnames('dbh-new-features', 'dbh-url-collection-section', className)}>
        <SectionHeader
          variant="features"
          title={titleText}
          actionText={actionTextValue}
          icon={ICON_FEATURE_BUBBLE}
          iconAlt={titleText}
          showAction={!featuresLoading && !!features?.length}
          onActionClick={handleOpenModal}
        />
        <FeaturesGrid
          loading={featuresLoading}
          items={emptyFeatures ? [] : homeItems}
          onItemClick={handleFeatureClick}
        />
      </div>
      {isNewFeaturesModalVisible && (
        <NewFeaturesModal
          visible={isNewFeaturesModalVisible}
          title={titleText}
          batchSize={batchSize}
          items={features}
          onClose={() => {
            setIsNewFeaturesModalVisible(false);
          }}
          onItemClick={handleFeatureClick}
        />
      )}
    </>
  );
};

export default NewFeatures;
